import React, { useState, useEffect, useMemo } from "react";
import { MessageSquareText, PhoneOutgoing, Contact, LoaderCircle, Loader, ChevronDown, NotebookPen } from "lucide-react";
import Spacing from "../Spacing";
import { fetchPatientOutreachInfo } from "../../services/patient";
import { exchangeDate, formatDate, numberWithCommas, notify, uniqueKey, convertSecondsToTime } from "../../helpers"
import { Config } from "../../Config";
import { addNewCallNote, fetchNewCallNoteOutreach } from "../../services/callNotes";
const aiImage = './images/AI.png';

export default function PatientOutReachModal({ 
  patient_id,
  account_id,
  location_id,
  startSchedular = false,
  setStartSchedular = () => {},
  last_name,
  first_name,
  dob,
  callId,
  callNoteCallType
}) {
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [dataCount, setDataCount] = useState({
    msgCount: 0,
    callNoteCount: 0,
    visitCount:0,
    ccmCount:0
  });
  const [outreachData, setOutreachData] = useState({});
  const [newOutreachCallData, setNewOutreachCallData] = useState(null);
  const [viewFullDetails, setViewFullDetails] = useState({});
  const [addCallNoteLoader, setAddCallNoteLoader] = useState(false);
  let ccmCount = 0;

  const handleNoteChange = (e) => setNote(e.target.value);
  const [filter, setFilter] = useState({
    patient_id:patient_id,
    account_id: account_id,
    location_id: location_id
  });

  const handleAddNote = async () => {
    if (note.trim()) {
      setAddCallNoteLoader(true);
      const formData = {
        patient_id: patient_id,
        account_id: account_id,
        location_id: location_id,
        notes: note,
      };

      try {
        const result = await addNewCallNote(formData);

        if (result) {
          setNote("");
          notify("success", "Call note added successfully.");
          loadPatientOutreachInfo(filter);
        }
      } catch (error) {
        if (error?.response && error?.response?.data && error?.response?.data?.detail) {
          notify("error", error?.response?.data?.detail, 0.5);
        }
      } finally {
        setAddCallNoteLoader(false);
      }
    }
  };

  const loadPatientOutreachInfo = async (getFilter) => {
    setFilter(getFilter);
    try {
      const response = await fetchPatientOutreachInfo(getFilter);
      if (response?.data) {
        setIsLoading(true);
        setDataCount({
          msgCount: response.data.message_count,
          callNoteCount: response.data.notes_count,
          visitCount:response.data.visit_count,
          ccmCount:response.data.ccm_count,
        });
        setOutreachData(response?.data?.data);
      }
    } catch (error) {
      // console.log("🚀 ~ loadPatientOutreachInfo ~ error:", error);
    } finally {
      setIsLoading(false);
    }
  };
  

  const capitalizeFirstLetter = (str) => {
    if (!str) return str; // Handle empty or undefined strings
    return str.charAt(0).toUpperCase() + str.slice(1);
  };


  useEffect(() => {
    loadPatientOutreachInfo(filter);
  }, [filter]);

  // Convert new line into break
  const renderTextIntoNewLines = (text) => {
    return text.split("\n")?.map((line, idx) => (
      <React.Fragment key={uniqueKey()}>
        <span>{line}</span><br />
      </React.Fragment>
    ));
  };

  const handleDropdownClick = (index) => { 
    setViewFullDetails((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const renderCCMTextIntoNewLines = (date, data) => {
    if (!data) return null;
    const currentCount = ccmCount++;
  
    const renderSection = (title, content) => (
      <div key={uniqueKey()} className="call_note_section">
        <h6>{capitalizeFirstLetter(title)}</h6>
        <p className="docpro_mb_0">{content}</p>
      </div>
    );

    const renderList = (title, items) => {
      return (
        <div key={uniqueKey()} className="call_note_section">
          <h6>{capitalizeFirstLetter(title)}</h6>
          <ul className="list">
            {(() => {
              try {
                if (Array.isArray(items)) {
                  return items.map((item) => {
                    if (typeof item === "string") {
                      return <li key={uniqueKey()} className="list-item">{item}</li>;
                    }
                    if (typeof item === "object" && item !== null) {
                      return (
                        <li key={uniqueKey()} className="list-item">
                          {Object.entries(item).map(([key, value]) => (
                            <div key={uniqueKey()}>
                              <strong>{key.replace(/_/g, " ")}:</strong> {JSON.stringify(value)}
                            </div>
                          ))}
                        </li>
                      );
                    }
                    return null;
                  });
                }
                if (typeof items === "object" && items !== null) {
                  return Object.entries(items).map(([key, value]) => (
                    <li key={uniqueKey()} className="list-item">
                      <strong>{value}</strong> - {key}
                    </li>
                  ));
                }
                if (typeof items === "string") {
                  return <li key={uniqueKey()} className="list-item">{items}</li>;
                }
              } catch (error) {
                console.error("Error rendering list:", error);
                return "";
              }
            })()}
          </ul>
        </div>
      );
    };
  
    const renderNestedObject = (title, obj) => (
      <div key={uniqueKey()} className="call_note_section">
        <h6>{capitalizeFirstLetter(title)}</h6>
        <ul className="list">
          {Object.entries(obj)?.map(([key, value]) => {
            if (typeof value === "string") {
              return (
                <li key={uniqueKey()} className="list-item">
                  <strong>{key.replace(/_/g, " ")}:</strong> {value}
                </li>
              );
            }
            if (Array.isArray(value)) {
              return (
                <li key={uniqueKey()} className="list-item">
                  <strong>{key.replace(/_/g, " ")}:</strong>
                  {renderList("", value)}
                </li>
              );
            }
            if (typeof value === "object") {
              return (
                <li key={uniqueKey()} className="list-item">
                  <strong>{key.replace(/_/g, " ")}:</strong>
                  {renderNestedObject("", value)}
                </li>
              );
            }
            return null;
          })}
        </ul>
      </div>
    );
  
    return (
      <div className="call_note_body">
        <div className="call_note_header">
          Chronic Care Management (CCM) Note
        </div>
        <div className="call_note_section info">
          <p>
            <strong>Date:</strong> {exchangeDate(date)}
          </p>
          <p>
            <strong>Patient Name:</strong> {first_name} {last_name}
          </p>
          <p>
            <strong>DOB:</strong> {exchangeDate(dob)}
          </p>
          <p>
            <strong>Time Spent:</strong>{" "}
            {data?.duration ? convertSecondsToTime(data?.duration) : "0 sec"}s
          </p>
          {data?.transcription_summary?.chief_complaint && (
            <p>
              <strong>Reason for Visit:</strong>{" "}
              {data?.transcription_summary?.chief_complaint}
            </p>
          )}
        </div>

        {!viewFullDetails[currentCount] &&
          data?.transcription_summary?.chief_complaint && (
            <div
              className="text-center pointer mt-2"
              onClick={() => handleDropdownClick(currentCount)}
            >
              <ChevronDown width={25} height={25} />
            </div>
          )}

        {viewFullDetails[currentCount] && (
          <>
            {data?.transcription_summary?.discussion_summary &&
              Object.entries(
                data?.transcription_summary?.discussion_summary
              )?.map(([key, value]) => {
                if (typeof value === "string") {
                  return renderSection(key.replace(/_/g, " "), value);
                }
                if (Array.isArray(value)) {
                  return renderList(key.replace(/_/g, " "), value);
                }
                if (typeof value === "object") {
                  let processedValue = value; 
                  
                  processedValue = Object.fromEntries(
                    Object.entries(value).map(([subKey, subValue]) => [
                      subKey,
                      Array.isArray(subValue)
                        ? subValue.join(", ")
                        : subValue,
                    ])
                  );
                   
                  return renderNestedObject( key.replace(/_/g, " "), processedValue );
                }
                return null;
              })}
            

            {data?.transcription_summary?.assessment_and_plan &&
              Object.entries(
                data?.transcription_summary?.assessment_and_plan
              )?.map(([key, value]) => {
                if (typeof value === "string") {
                  return renderSection(key.replace(/_/g, " "), value);
                }
                if (Array.isArray(value)) {
                  return renderList(key.replace(/_/g, " "), value);
                }
                if (typeof value === "object") {
                  return renderNestedObject(key.replace(/_/g, " "), value);
                }
                return null;
              })}

            {data?.transcription_summary?.icd_codes &&
              renderList("ICD Codes", data?.transcription_summary?.icd_codes)}
          </>
        )}
      </div>
    );
  };
  
  const NewCallNoteOutreach = async () => {
    const formData = {
      account_id: account_id,
      location_id: location_id,
      call_sid: callId,
      call_type: callNoteCallType
    };

    try {
      const response = await fetchNewCallNoteOutreach(formData);
      
      if(response){
        if(!response?.data?.detail) {
          if(callNoteCallType === "ccm") {
            setNewOutreachCallData(response?.data?.transcription_summary_json);
          } else {
            setNewOutreachCallData(response?.data?.notes);
          }
          setStartSchedular(false);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ NewCallNoteOutreach ~ error:", error);
    }
  }

  useEffect(() => {
    if(startSchedular){
      NewCallNoteOutreach();
      const intervalId = setInterval(NewCallNoteOutreach, 3000);

      return () => clearInterval(intervalId);
    }
  }, [startSchedular]);

  const memoizedOutreachData = useMemo(() => {
    return Object.entries(outreachData)?.map(([date,details]) => (
      <div key={uniqueKey()}>
        <div className="note-entry">
          <p className="note-date docpro_ternary_color">{exchangeDate(date)}</p>
          {details.message && (
            <>
              {details.message?.map((msg,idx) => (
                <div key={uniqueKey()} className="docpro_message_item note-content">
                  <div className="docpro_message_item_out">
                    <div className="docpro_message_item_in">
                      <div className="docpro_message_item_avatar outreach_avatar">
                        <MessageSquareText size={18} className="success" />
                      </div>
                      <div className="docpro_message_item_text">
                        {renderTextIntoNewLines(msg)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <Spacing lg="10" sm="10" />
            </>
          )}
          {details.call_notes && ( 
            <>
              {details.call_notes?.map((notes,idx) => (
                notes?.call_note?.length > 0 && (
                  <div key={uniqueKey()} className="docpro_message_item note-content">
                    <div className="docpro_message_item_out">
                      <div className="docpro_message_item_in">
                        <div className="docpro_message_item_avatar outreach_avatar">
                          <PhoneOutgoing size={17} className="primary" />
                        </div>
                        <div className="docpro_message_item_text">
                          {renderTextIntoNewLines(notes?.call_note)}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              ))}
              <Spacing lg="10" sm="10" />
            </>
          )}
          {details.ccm && (
            <>
              {details.ccm?.map((ccm_object, idx) => (
                <div
                  key={uniqueKey()} // Use a unique field like ccm.date if available
                  className="docpro_message_item note-content"
                >
                  <div className="docpro_message_item_out">
                    <div className="docpro_message_item_in">
                      <div className="docpro_message_item_avatar outreach_avatar">
                        <PhoneOutgoing size={17} className="primary" />
                      </div>
                      <div className="docpro_message_item_text">
                        {renderCCMTextIntoNewLines(date,ccm_object)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <Spacing lg="10" sm="10" />
            </>
          )}
          {details.visit && ( 
            <div className="docpro_message_item note-content">
              <div className="docpro_message_item_out">
                <div className="docpro_message_item_in">
                  <div className="docpro_message_item_avatar outreach_avatar">
                    <Contact size={18} className="purple" />
                  </div>
                  <div className="docpro_message_item_text">
                    <span>Patient visited clinic</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <Spacing lg="30" sm="30" />
      </div>
    ));
  }, [outreachData, viewFullDetails]);
  

  return (
    <>
      {!isLoading ? (
        <div className='docpro_card'>
          <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_6">
            <div className="docpro_grid_item">
              <h3 className="docpro_mb_0">{numberWithCommas(dataCount.msgCount)}</h3>
              <p className="docpro_mb_0 docpro_ternary_color">Messages</p>
            </div>
            <div className="docpro_grid_item">
              <h3 className="docpro_mb_0">{numberWithCommas(dataCount.callNoteCount)}</h3>
              <p className="docpro_mb_0 docpro_ternary_color">Call Notes</p>
            </div>
            <div className="docpro_grid_item">
              <h3 className="docpro_mb_0">{numberWithCommas(dataCount.visitCount)}</h3>
              <p className="docpro_mb_0 docpro_ternary_color">Visits</p>
            </div>
            <div className="docpro_grid_item">
              <h3 className="docpro_mb_0">{numberWithCommas(dataCount.ccmCount)}</h3>
              <p className="docpro_mb_0 docpro_ternary_color">CCM</p>
            </div>
          </div>
          <Spacing lg="20" sm="20" />
          <hr />

          <Spacing lg="20" sm="20" />
          {/* Add Note Section */}
          <div className="docpro-row add-note-section">
            <textarea
              className="docpro_form_field size_lg note-textarea"
              cols="30"
              rows="2"
              value={note}
              onChange={handleNoteChange}
              placeholder="Write your note..."
              style={{ resize: 'none' }}
            ></textarea>
            <Spacing lg="15" sm="15" />
            <div className="text-right">
              <button
                type="button"
                onClick={handleAddNote}
                className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn add-note-button"
                style={{ padding: '10px 40px' }}
                disabled={addCallNoteLoader ? true : false}
              >
                <NotebookPen width={18} height={18} />
                <span>Add Note</span>
                {addCallNoteLoader && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
            </div>
          </div>
          <Spacing lg="40" sm="40" />

          {/* Notes Section */}

          <div className="docpro-row notes-section">
            {startSchedular ? ( 
              <>
                <p className="note-date docpro_ternary_color">{exchangeDate(formatDate(new Date()))}</p>
                <div className="docpro_message_item note-content">
                  <div className="docpro_message_item_out">
                    <div className="docpro_message_item_in">
                      <div className="docpro_message_item_avatar outreach_avatar">
                        <PhoneOutgoing size={17} className="primary" />
                      </div>
                      <div className="docpro_message_item_text">
                        <img src={aiImage} width={40} alt="AI" style={{ margin: '-6px 0 0 -6px' }} />
                        <div className="flex items-center mt-2 docpro_ternary_color">
                          <span className="docpro_loading mr-2" style={{ position: 'inherit', width: 'inherit'}}>
                            <Loader width={22} height={22}/>
                          </span>
                          <b className="docpro_secondary_color">{Config?._AppName} AI is generating summary and note...</b>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Spacing lg="20" sm="20" />
              </>
            ) : (
              newOutreachCallData && (
                <>
                  <p className="note-date docpro_ternary_color">{exchangeDate(formatDate(new Date()))}</p>
                  <div className="docpro_message_item note-content">
                    <div className="docpro_message_item_out">
                      <div className="docpro_message_item_in">
                        <div className="docpro_message_item_avatar outreach_avatar">
                          <PhoneOutgoing size={17} className="primary" />
                        </div>
                        <div className="docpro_message_item_text">
                          {callNoteCallType === "ccm" ? 
                            renderCCMTextIntoNewLines(formatDate(new Date()),newOutreachCallData)
                          :
                            renderTextIntoNewLines(newOutreachCallData)
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                  <Spacing lg="20" sm="20" />
                </>
              )
            )}

            {memoizedOutreachData}
          </div>
        </div>
      ):(
        <div className="flex items-center loading" style={{ width: '100%', height: '100%',justifyContent: 'center'}}>
          <span className="docpro_loading" style={{ position: 'inherit',marginTop:"13rem"}}>
            <LoaderCircle width={40} height={40} style={{color:"#369eff"}} />
          </span>
        </div>
      )}
    </>
  );
}
